<script setup lang="ts">
import ElementsLogo from '../../elements/logo/index.vue'
import { useScriptTag } from '@vueuse/core'

declare global {
  interface Window {
    Trustpilot?: any
  }
}

export type Props = {
  hideTrustpilot?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hideTrustpilot: false,
})

const $trustpilot = useTemplateRef('$trustpilot')

useScriptTag(
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
  // on script tag loaded.
  (el: HTMLScriptElement) => {
    if (window?.Trustpilot && $trustpilot.value) {
      window?.Trustpilot?.loadFromElement?.($trustpilot.value, true)
    }
  },
  {
    async: true,
    manual: props.hideTrustpilot,
  }
)
</script>

<template>
  <footer class="mt-auto overflow-clip">
    <div class="bg-white p-10">
      <div v-if="!hideTrustpilot" class="container">
        <div class="lg:py-20" v-once>
          <div
            ref="$trustpilot"
            class="trustpilot-widget"
            data-locale="da-DK"
            data-template-id="53aa8912dec7e10d38f59f36"
            data-businessunit-id="60d06b2f84a9e70001a23ef7"
            data-style-height="140px"
            data-style-width="100%"
            data-token="d6df572a-0a5a-4171-86ad-f87807f8d0d4"
            data-tags="SelectedReview"
            data-review-languages="da"
          >
            <a
              href="https://dk.trustpilot.com/review/www.saphe.com"
              target="_blank"
              rel="noopener"
              >Trustpilot</a
            >
          </div>
        </div>
      </div>
      <div
        v-if="!hideTrustpilot"
        class="h-px bg-foreground-neutral-secondary/20 mt-10 mb-4 -mx-10"
      ></div>
      <div class="container">
        <div class="w-full flex items-center justify-center lg:justify-between">
          <div>
            <ElementsLogo
              class="size-[clamp(4rem,4vw,10rem)] text-neutral-black"
              alt="Saphe logo"
            />
          </div>
          <!-- <div>
            <svg
              width="40"
              height="24"
              viewBox="0 0 40 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.53319 11.2348H1.16314C0.925256 11.2348 0.740234 11.0414 0.740234 10.8129C0.740234 10.7337 0.757856 10.6546 0.801908 10.5931C1.44508 9.54692 3.01336 7.3667 6.00896 5.18648H6.01777C6.12349 5.09857 9.26887 2.35571 14.8283 1.22165C14.8283 1.22165 22.5376 -0.589343 29.9825 2.85681C29.9825 2.85681 36.2116 5.96011 38.8107 10.6107C38.9253 10.8129 38.846 11.0766 38.6433 11.1821C38.5816 11.2173 38.5112 11.2348 38.4407 11.2348H36.564C35.9561 11.2348 35.401 10.9096 35.0927 10.3909C34.5464 9.43264 33.3305 7.76231 30.8548 5.98648C30.8548 5.98648 26.379 2.68978 19.3482 3.38428C19.3482 3.38428 15.3482 3.67439 12.0354 5.74033H12.0266C11.9385 5.78428 9.92085 6.77769 6.99574 10.5052C6.65213 10.9711 6.10587 11.2348 5.53319 11.2348Z"
                fill="#44C0F0"
              />
              <path
                d="M34.0353 12.9492H38.4054C38.6433 12.9492 38.8283 13.1426 38.8283 13.3712C38.8283 13.4503 38.8107 13.5206 38.7666 13.591C38.1234 14.6371 36.5552 16.8174 33.5596 18.9976L33.5508 19.0064C33.4274 19.1206 30.2732 21.8459 24.749 22.9712C24.749 22.9712 17.0485 24.7822 9.5948 21.336C9.5948 21.336 3.36573 18.2327 0.76661 13.5822C0.652073 13.38 0.731368 13.1163 0.934011 13.0108C0.995685 12.9756 1.06617 12.958 1.13665 12.958H2.99569C3.60362 12.958 4.16749 13.2833 4.46705 13.8108C5.01331 14.769 6.22917 16.4393 8.70494 18.2152C8.70494 18.2152 13.1807 21.5119 20.2027 20.8174C20.2027 20.8174 24.2027 20.5272 27.5155 18.4613L27.5243 18.4525C27.6477 18.391 29.6477 17.3888 32.5463 13.6877C32.9164 13.2217 33.4626 12.958 34.0353 12.9492Z"
                fill="#44C0F0"
              />
              <path
                d="M24.2114 13.9438C23.7268 13.9438 23.2687 14.1812 22.9867 14.5856C22.511 15.2801 21.8149 15.7988 21.0132 16.0625C21.0132 16.0625 18.6079 16.9328 16.837 14.9109L25.9471 10.8317C26.3083 10.6647 26.4757 10.2427 26.3436 9.87349C24.9779 6.31305 20.9779 4.52844 17.4096 5.89107C13.8414 7.25371 12.0528 11.2449 13.4184 14.8054C14.7841 18.3658 18.7841 20.1504 22.3524 18.7878C24.3259 18.0405 25.8414 16.4317 26.4845 14.4185C26.5462 14.2163 26.4317 14.0054 26.2378 13.9438C26.2026 13.935 26.1585 13.9262 26.1233 13.9262L24.2114 13.9438ZM15.9118 12.379C15.9118 10.2427 17.6475 8.50206 19.7885 8.50206C20.9515 8.50206 22.044 9.01195 22.7841 9.90865L15.9647 12.9856C15.9295 12.7834 15.9118 12.5812 15.9118 12.379Z"
                fill="#44C0F0"
              />
            </svg>
          </div> -->
        </div>
        <div
          class="h-px bg-foreground-neutral-secondary/20 mt-4 mb-10 w-full"
        ></div>
        <div
          class="w-full flex max-lg:flex-col items-center justify-between text-foreground-neutral-secondary text-sm max-lg:gap-y-4"
        >
          <div class="max-lg:text-center">
            Saphe A/S · Gasværksvej 46 · 9000 Aalborg, Danmark · CVR: 36704586
          </div>
          <div
            class="flex flex-wrap items-center max-lg:justify-center gap-x-4"
          >
            <a href="https://checkout.saphe.com/da/privacy-policy" target="_blank" class="hover:underline">Privatlivspolitik</a>
            <a href="https://checkout.saphe.com/da/terms-of-sale" target="_blank" class="hover:underline">Handelsbetingelser</a>
            <a href="https://checkout.saphe.com/da/terms-and-conditions/" target="_blank" class="hover:underline">Vilkår og betingelser</a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
